import { z } from 'zod';
export const DOCUMENTS = {
    brief: {
        name: 'The session brief',
        reader: 'therapist',
        blurb: 'For a therapist link, before an appointment: the stretch since the last one in the app’s voice, the days, four figures, the homework as written, the worksheets in their template, the threads, and one question computed from the figures.',
    },
    week: {
        name: 'The week',
        reader: 'partner',
        blurb: 'For a partner link, as one page: the reading that level already gets, the seven days, and what helps.',
    },
    card: {
        name: 'The card',
        reader: 'friend',
        blurb: 'For a friend link: how he seems today, the last seven days, what helps and a line from him, on one card, with the page behind it.',
    },
    month: {
        name: 'The month',
        reader: 'owner',
        blurb: 'For you, once a month is over: the month in the app’s voice with your threads as chapters, every day’s ending, three or four of your own sentences from its highest and lowest days, and the figures.',
    },
};
/** Which document a link's page offers, by its role. Null for a link with no role, or a friend. */
export function documentForRole(role) {
    if (role === 'therapist')
        return 'brief';
    if (role === 'partner')
        return 'week';
    if (role === 'friend')
        return 'card';
    return null;
}
export const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const isoMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
/** The brief's default reach when no previous session is recorded: two weeks. */
export const BRIEF_DEFAULT_DAYS = 14;
/**
 * The stretch a session brief covers: since the last session, or the default
 * fortnight; up to today, or the session date if it is already past. A pure
 * function of three dates, so the page and the server can agree on it.
 */
/**
 * The session dates as they stand TODAY, once a passed appointment has moved.
 *
 * A pure function, and used on BOTH sides, because it was neither. The roll
 * lived in the owner's listing route and wrote to the database, so what the
 * therapist's own page said depended on whether Nick had happened to open his
 * Circle screen:
 *
 *   before he opened it   next = 1 Nov (past), last = 20 Oct -> brief 20 Oct..1 Nov
 *   after he opened it    next = none,         last = 1 Nov  -> brief 1 Nov..today
 *
 * Same link, same moment, two different windows, decided by an unrelated
 * action of somebody else's. Derived here and the derivation is the truth;
 * the owner's read still PERSISTS it, so the stored row catches up, but
 * nothing depends on that having happened.
 *
 * Strictly before today, so the day of the appointment still reads as next.
 * The next is cleared rather than guessed: the app does not know when they
 * next meet, and a guessed appointment on a therapist's page is worse than an
 * empty field that asks.
 */
export function rolledSession(sessionOn, lastSessionOn, today) {
    if (sessionOn && sessionOn < today)
        return { sessionOn: null, lastSessionOn: sessionOn };
    return { sessionOn: sessionOn ?? null, lastSessionOn: lastSessionOn ?? null };
}
export function briefWindow(today, sessionOn, lastSessionOn) {
    const to = sessionOn && sessionOn < today ? sessionOn : today;
    const from = lastSessionOn && lastSessionOn < to ? lastSessionOn : shiftDay(to, -(BRIEF_DEFAULT_DAYS - 1));
    return { from, to };
}
export function shiftDay(day, n) {
    const d = new Date(`${day}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
/** The calendar month a day is in, and the first and last day of a month. */
export function monthOf(day) {
    return day.slice(0, 7);
}
export function monthBounds(month) {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}
export function shiftMonth(month, n) {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
export const ONE_THING_RULES = [
    { id: 'belief_held', when: 'a worksheet was finished and the belief did not move' },
    { id: 'unfinished', when: 'a worksheet was started and left without an answer' },
    { id: 'lowest_unwritten', when: 'the lowest day in the window has nothing written beside it' },
    { id: 'homework_stopped', when: 'a piece of homework stopped before its end date' },
];
export function oneThing(input) {
    const held = input.worksheets.find((w) => w.isComplete && w.beliefBefore !== null && w.beliefAfter !== null && w.beliefAfter >= w.beliefBefore);
    if (held)
        return { id: 'belief_held', text: `The worksheet of ${longDay(held.day)}: the belief did not move. What held it in place?` };
    const open = input.worksheets.find((w) => !w.isComplete);
    if (open)
        return { id: 'unfinished', text: `The worksheet of ${longDay(open.day)} was left without an answer. What stopped it?` };
    if (input.lowestDay && !input.lowestDay.written) {
        return { id: 'lowest_unwritten', text: `${longDay(input.lowestDay.day)} ended lowest and has nothing written beside it. What was that day?` };
    }
    const stopped = input.homework.find((h) => h.lastAnswerDay !== null && h.endsOn !== null && h.lastAnswerDay < h.endsOn && h.endsOn < input.today);
    if (stopped)
        return { id: 'homework_stopped', text: `“${stopped.title}” stopped on ${longDay(stopped.lastAnswerDay)}, with days still to run. What changed?` };
    return null;
}
export function longDay(day) {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
}
//# sourceMappingURL=documents.js.map